import { useState } from "react";

import useAuth from "contexts/Auth/useAuth";

/**
 * The three ways a download can fail, as display strings.
 *
 * Passed in rather than built here because the two callers do not speak the
 * same language: /internal is English by design, /admin/statistics goes through
 * i18next. `failed` takes the status because the caller cannot know it at
 * render time — the hook has it, the wording belongs to the caller.
 */
export interface CsvDownloadMessages {
	/** 429 — the hourly export budget is spent. */
	rateLimited: string;
	/** Any other non-2xx answer. */
	failed: (status: number) => string;
	/** The request never completed. */
	network: string;
}

const DEFAULT_MESSAGES: CsvDownloadMessages = {
	rateLimited: "Too many exports in the last hour. Try again later.",
	failed: (status) => `The export failed (${status}).`,
	network: "The export failed. Check your connection and try again.",
};

/**
 * Download a CSV from an authenticated endpoint.
 *
 * Not a plain `<a href download>`: the API authenticates on the Authorization
 * header, which a link cannot send. `getToken` on the server would also accept
 * `?token=`, but that writes a valid JWT into browser history and the nginx
 * access log — so: fetch, blob, synthetic click.
 *
 * Shared by every export tile. They differ only in path, filename and wording,
 * and duplicating the blob dance once per report is how one of them ends up
 * quietly missing the revokeObjectURL.
 *
 * `headers` is for the organization-scoped reports: those routes sit behind
 * `hasOrganizationRole`, which reads the organization from `x-org` and answers
 * 400 without it. The staff exports on /internal have no organization and pass
 * nothing.
 */
const useCsvDownload = (
	path: string,
	fallbackFilename: () => string,
	messages: CsvDownloadMessages = DEFAULT_MESSAGES,
	headers: Record<string, string> = {},
) => {
	const { auth } = useAuth();
	const [downloading, setDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const download = async () => {
		setDownloading(true);
		setError(null);

		try {
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL}${path}`,
				{
					headers: {
						...headers,
						Authorization: `Bearer ${auth?.token}`,
					},
				},
			);

			if (!res.ok) {
				setError(
					res.status === 429
						? messages.rateLimited
						: messages.failed(res.status),
				);
				return;
			}

			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			// The server sends the real name in Content-Disposition, but the API
			// is on another origin and a browser only exposes that header to
			// script when the server lists it in Access-Control-Expose-Headers —
			// hence the fallback, which must produce the same string.
			const disposition = res.headers.get("Content-Disposition");
			const match = disposition
				? /filename="?([^"]+)"?/.exec(disposition)
				: null;
			link.download = match ? match[1] : fallbackFilename();
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			setError(messages.network);
		} finally {
			setDownloading(false);
		}
	};

	return { download, downloading, error };
};

export default useCsvDownload;
